/**
 * OwnersPanel — Settings → Owners
 * List / create / edit owner entities; link from existing project owner strings.
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createOwner,
  createOwnerFromString,
  listOwners,
  listUnlinkedOwnerStrings,
  updateOwner,
  type Owner,
  type UnlinkedOwnerString,
} from '../../../api/owners'

type FormState = {
  name: string
  contact_names: string
  email: string
  phone: string
  address: string
  default_terms: string
  pays_by: string
}

const EMPTY: FormState = {
  name: '',
  contact_names: '',
  email: '',
  phone: '',
  address: '',
  default_terms: 'Net 30',
  pays_by: '',
}

function toForm(o: Owner | null): FormState {
  if (!o) return { ...EMPTY }
  return {
    name: o.name || '',
    contact_names: o.contact_names || '',
    email: o.email_pending || o.email_is_placeholder ? '' : (o.email || ''),
    phone: o.phone || '',
    address: o.address || '',
    default_terms: o.default_terms || 'Net 30',
    pays_by: o.pays_by || '',
  }
}

export default function OwnersPanel() {
  const qc = useQueryClient()
  const { data: owners = [], isLoading } = useQuery({
    queryKey: ['owners'],
    queryFn: listOwners,
  })
  const { data: unlinked = [] } = useQuery({
    queryKey: ['owners-unlinked'],
    queryFn: listUnlinkedOwnerStrings,
  })

  const [editing, setEditing] = useState<Owner | null>(null)
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState<FormState>(EMPTY)
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const [errMsg, setErrMsg] = useState('')

  useEffect(() => {
    if (creating) setForm(EMPTY)
    else if (editing) setForm(toForm(editing))
  }, [creating, editing])

  function set(k: keyof FormState, v: string) {
    setForm((f) => ({ ...f, [k]: v }))
  }

  async function save() {
    if (!form.name.trim()) {
      setErrMsg('Owner name is required')
      setStatus('error')
      return
    }
    setStatus('saving')
    setErrMsg('')
    try {
      const payload = {
        name: form.name.trim(),
        contact_names: form.contact_names.trim() || null,
        email: form.email.trim() || null,
        email_is_placeholder: false,
        phone: form.phone.trim() || null,
        address: form.address.trim() || null,
        default_terms: form.default_terms.trim() || null,
        pays_by: form.pays_by.trim() || null,
      }
      if (editing) await updateOwner(editing.id, payload)
      else await createOwner(payload)
      await qc.invalidateQueries({ queryKey: ['owners'] })
      await qc.invalidateQueries({ queryKey: ['owners-unlinked'] })
      setCreating(false)
      setEditing(null)
      setStatus('saved')
      setTimeout(() => setStatus('idle'), 2000)
    } catch (e) {
      setErrMsg((e as Error).message || 'Save failed')
      setStatus('error')
    }
  }

  async function linkFromString(row: UnlinkedOwnerString) {
    setStatus('saving')
    try {
      await createOwnerFromString({ owner_name: row.owner_name, name: row.owner_name })
      await qc.invalidateQueries({ queryKey: ['owners'] })
      await qc.invalidateQueries({ queryKey: ['owners-unlinked'] })
      setStatus('saved')
      setTimeout(() => setStatus('idle'), 2000)
    } catch (e) {
      setErrMsg((e as Error).message || 'Link failed')
      setStatus('error')
    }
  }

  const showForm = creating || !!editing
  const navigate = useNavigate()

  return (
    <div id="owners" className="panel" data-testid="panel-owners" style={{ marginTop: 14 }}>
      <div className="panel-h">
        <div className="panel-tt">Owners</div>
        <span className="panel-st">Property owners / clients for projects</span>
      </div>
      <div className="panel-bp">
        {isLoading ? <div className="set-skeleton">Loading owners…</div> : null}

        {!showForm && (
          <>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }} data-testid="owners-list">
              {owners.map((o) => (
                <li
                  key={o.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px 0',
                    borderBottom: '1px solid var(--line)',
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 600 }}>{o.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--ink3)' }}>
                      {o.project_count ?? 0} project{(o.project_count ?? 0) === 1 ? '' : 's'}
                      {o.email_pending || o.email_is_placeholder
                        ? ' · contact email pending'
                        : o.email
                          ? ` · ${o.email}`
                          : ''}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      className="btn b-ol btn-sm"
                      data-testid={`owner-statement-${o.id}`}
                      onClick={() => navigate(
                        `/statements/owner?owner_id=${o.id}&owner=${encodeURIComponent(o.name)}`,
                      )}
                    >
                      Statement for all jobs
                    </button>
                    <button
                      type="button"
                      className="btn b-gh"
                      data-testid={`owner-edit-${o.id}`}
                      onClick={() => { setEditing(o); setCreating(false) }}
                    >
                      Edit
                    </button>
                  </div>
                </li>
              ))}
              {!owners.length && (
                <li style={{ fontSize: 13, color: 'var(--ink3)', padding: '8px 0' }}>
                  No owners yet. Create one or link from a project owner name.
                </li>
              )}
            </ul>

            {unlinked.length > 0 && (
              <div style={{ marginTop: 18 }} data-testid="owners-unlinked">
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                  Link from existing project names
                </div>
                {unlinked.map((u) => (
                  <div
                    key={u.owner_name}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 0',
                      fontSize: 13,
                    }}
                  >
                    <span>
                      {u.owner_name}
                      {' '}
                      <span style={{ color: 'var(--ink3)' }}>
                        ({u.project_count})
                      </span>
                    </span>
                    <button
                      type="button"
                      className="btn b-gh"
                      data-testid={`owner-link-${u.owner_name}`}
                      onClick={() => linkFromString(u)}
                      disabled={status === 'saving'}
                    >
                      Create from this name
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div style={{ marginTop: 16 }}>
              <button
                type="button"
                className="btn b-dk"
                data-testid="owners-create"
                onClick={() => { setCreating(true); setEditing(null) }}
              >
                Add Owner
              </button>
            </div>
          </>
        )}

        {showForm && (
          <div data-testid="owners-form">
            <div className="fld-grid">
              <div className="fld">
                <label className="fld-l">Owner name *</label>
                <input className="fld-i" value={form.name} onChange={(e) => set('name', e.target.value)} data-testid="owner-name" />
              </div>
              <div className="fld">
                <label className="fld-l">Contacts</label>
                <input className="fld-i" placeholder="Mr. A; Ms. B" value={form.contact_names} onChange={(e) => set('contact_names', e.target.value)} data-testid="owner-contacts" />
              </div>
            </div>
            <div className="fld-grid" style={{ marginTop: 12 }}>
              <div className="fld">
                <label className="fld-l">Email</label>
                <input className="fld-i mono" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} data-testid="owner-email" />
                {editing && (editing.email_pending || editing.email_is_placeholder) && !form.email && (
                  <div className="fld-h" style={{ color: 'var(--amber, #b45309)' }}>Contact email pending</div>
                )}
              </div>
              <div className="fld">
                <label className="fld-l">Phone</label>
                <input className="fld-i mono" value={form.phone} onChange={(e) => set('phone', e.target.value)} data-testid="owner-phone" />
              </div>
            </div>
            <div className="fld" style={{ marginTop: 12 }}>
              <label className="fld-l">Address</label>
              <input className="fld-i" value={form.address} onChange={(e) => set('address', e.target.value)} data-testid="owner-address" />
            </div>
            <div className="fld-grid" style={{ marginTop: 12 }}>
              <div className="fld">
                <label className="fld-l">Default terms</label>
                <input className="fld-i" value={form.default_terms} onChange={(e) => set('default_terms', e.target.value)} data-testid="owner-terms" />
              </div>
              <div className="fld">
                <label className="fld-l">Pays by</label>
                <input className="fld-i" placeholder="ACH/check" value={form.pays_by} onChange={(e) => set('pays_by', e.target.value)} data-testid="owner-pays-by" />
              </div>
            </div>
            {errMsg && (
              <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 10 }}>{errMsg}</div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button type="button" className="btn b-dk" disabled={status === 'saving'} onClick={save} data-testid="owner-save">
                {status === 'saving' ? 'Saving…' : status === 'saved' ? 'Saved ✓' : 'Save'}
              </button>
              <button
                type="button"
                className="btn b-gh"
                onClick={() => { setCreating(false); setEditing(null); setStatus('idle') }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
